
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/contexts/LanguageContext';
import { Lock, Key } from 'lucide-react';
import { toast } from 'sonner';

const SecuritySection = () => {
  const { t } = useLanguage();
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handlePasswordChange = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error(t('passwords_dont_match', 'كلمات المرور غير متطابقة', 'Passwords do not match'));
      return;
    }

    try {
      // Implement password change logic here
      toast.success(t('password_changed', 'تم تغيير كلمة المرور', 'Password changed successfully'));
      setIsChangingPassword(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(t('password_change_failed', 'فشل في تغيير كلمة المرور', 'Failed to change password'));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Lock className="h-5 w-5" />
          {t('security_settings', 'إعدادات الأمان', 'Security Settings')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <h4 className="font-medium mb-2">{t('change_password', 'تغيير كلمة المرور', 'Change Password')}</h4>
          {!isChangingPassword ? (
            <Button variant="outline" onClick={() => setIsChangingPassword(true)}>
              <Key className="h-4 w-4 mr-2" />
              {t('change_password', 'تغيير كلمة المرور', 'Change Password')}
            </Button>
          ) : (
            <div className="space-y-4 max-w-md">
              <div>
                <Label htmlFor="currentPassword">{t('current_password', 'كلمة المرور الحالية', 'Current Password')}</Label>
                <Input
                  id="currentPassword"
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="newPassword">{t('new_password', 'كلمة المرور الجديدة', 'New Password')}</Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="confirmPassword">{t('confirm_new_password', 'تأكيد كلمة المرور الجديدة', 'Confirm New Password')}</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={handlePasswordChange}>
                  {t('save_password', 'حفظ كلمة المرور', 'Save Password')}
                </Button>
                <Button variant="outline" onClick={() => setIsChangingPassword(false)}>
                  {t('cancel', 'إلغاء', 'Cancel')}
                </Button>
              </div>
            </div>
          )}
        </div>

        <div>
          <h4 className="font-medium mb-2">{t('active_sessions', 'الجلسات النشطة', 'Active Sessions')}</h4>
          <div className="space-y-2">
            <div className="flex items-center justify-between p-3 border rounded">
              <div>
                <p className="font-medium">{t('current_session', 'الجلسة الحالية', 'Current Session')}</p>
                <p className="text-sm text-muted-foreground">
                  {t('last_active', 'آخر نشاط', 'Last active')}: {new Date().toLocaleString()}
                </p>
              </div>
              <Badge variant="secondary">{t('current', 'الحالية', 'Current')}</Badge>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SecuritySection;
